import React, { useEffect, useState, useCallback } from 'react';
import TradingViewChart from '../Components/Charts/TradingViewChart.tsx';
import './Market.css';
import { useLoading } from '../../contexts/LoadingContext.tsx';
import axios from 'axios';
import TimeLimit from './OrderConfirmation.tsx';

interface MarketOrderItem {
  icon: string;
  name: string;
  lastPrice: number;
  change: number;
  symbol: string;
}

const Market = () => {
  const [data, setData] = useState<MarketOrderItem[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [latestValue, setLatestValue] = useState<number | null>(null);
  const [webSockets, setWebSockets] = useState<{ [key: string]: WebSocket }>({});
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderType, setOrderType] = useState<'BuyUp' | 'BuyDown' | null>(null);
  const { setIsLoading } = useLoading();

  useEffect(() => {
    const fetchMarketData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          'https://min-api.cryptocompare.com/data/top/mktcapfull?limit=10&tsym=USD',
          {
            headers: {
              'authorization': 'Apikey ee48a129618ba0a14e55260ebca19aa2717d3b36e12df129020ceed97d102fdb',
            },
          }
        );
  
        const marketData = response.data.Data.map((item: any) => ({
          icon: item.CoinInfo?.ImageUrl ? `https://www.cryptocompare.com${item.CoinInfo.ImageUrl}` : '',
          name: item.CoinInfo?.FullName || 'Unknown Coin',
          symbol: item.CoinInfo?.Name || 'N/A',
          lastPrice: item.RAW?.USD?.PRICE ?? 0,
          change: item.RAW?.USD?.CHANGEPCT24HOUR ?? 0,
        }));
  
        // ✅ Adding Gold Price with Correct Symbol (XAUUSDT)
        const goldMineCoin: MarketOrderItem = {
          icon: 'images.png',
          name: 'XAU/USD',
          lastPrice: 150.75,
          change: 2.34,
          symbol: 'XAUUSD', // ✅ FIXED SYMBOL FOR BINANCE
        };
  
        marketData.push(goldMineCoin);
        setData(marketData);
  
        // ✅ WebSocket for live price updates
        marketData.forEach((item: MarketOrderItem) => {
          if (!webSockets[item.symbol]) {
            const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${item.symbol.toLowerCase()}@trade`);
  
            ws.onopen = () => console.log(`WebSocket connected for ${item.symbol}`);
  
            ws.onmessage = (event) => {
              try {
                const data = JSON.parse(event.data);
                const latestPrice = data.p ? parseFloat(data.p) : item.lastPrice;
  
                setData((prevData) =>
                  prevData.map((prevItem) =>
                    prevItem.symbol === item.symbol ? { ...prevItem, lastPrice: latestPrice } : prevItem
                  )
                );
              } catch (error) {
                console.error('WebSocket message error:', error);
              }
            };
  
            ws.onerror = (error) => console.error('WebSocket error:', error);
            ws.onclose = () => console.log(`WebSocket closed for ${item.symbol}`);
  
            setWebSockets((prevWs) => ({ ...prevWs, [item.symbol]: ws }));
          }
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchMarketData();
    const intervalId = setInterval(fetchMarketData, 10000);
  
    return () => {
      clearInterval(intervalId);
      Object.values(webSockets).forEach((ws) => ws.close());
    };
  }, [webSockets, setIsLoading]);
  

  const handleCurrencyClick = useCallback((item: MarketOrderItem) => {
    if (item.symbol === "XAUUSD") {
      setSelectedSymbol("XAUUSD"); // ✅ Ensure XAUUSD is used directly
    } else {
      setSelectedSymbol(item.symbol);
    }
    setLatestValue(item.lastPrice);
    setIsFullscreen(true);
  }, []);
  

  const handleBuyUpClick = useCallback(() => {
    setOrderType('BuyUp');
    setShowOrderModal(true);
  }, []);

  const handleBuyDownClick = useCallback(() => {
    setOrderType('BuyDown');
    setShowOrderModal(true);
  }, []);

  return (
    <div className={`market-order ${isFullscreen ? 'fullscreen' : ''}`}>
      {isFullscreen ? (
        <div className="fullscreen-overlay">
          <button className="back-button" onClick={() => setIsFullscreen(false)}>
            &larr; Back
          </button>
          <div className="chart-container">
            <h3 className="chart-title">Currency Symbol: {selectedSymbol?.toUpperCase()}</h3>
            <TradingViewChart selectedSymbol={selectedSymbol} onValueUpdate={setLatestValue} />
          </div>
          <div className="trading-buttons">
            <button className="buy-up-button" onClick={handleBuyUpClick}>
              Buy Up
            </button>
            <button className="buy-down-button" onClick={handleBuyDownClick}>
              Buy Down
            </button>
          </div>
          {latestValue !== null && (
            <div className="latest-price">
              <h3>Latest Price:</h3>
              <span>${latestValue.toFixed(2)}</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="header">
            <h2>Market Order</h2>
            <button className="link-like">More</button>
          </div>
          <div className="tabs">
            <div className="tab active">Time Limit</div>
            <div className="tab">Derivatives</div>
          </div>
          <div className="table">
            <div className="table-header">
              <span>Products</span>
              <span>Last Price</span>
              <span>Today Change</span>
            </div>
            {data.map((item, index) => (
              <div key={index} className="table-row" onClick={() => handleCurrencyClick(item)}>
                <span className="product">
                  <img src={item.icon} alt={item.name} /> {item.name}
                </span>
                <span className="last-price">${(item.lastPrice ?? 0).toFixed(2)}</span>
                <span className={`today-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                  {item.change.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
          {selectedSymbol && (
            <div className="selected-symbol">
              <h3>Selected Currency Symbol:</h3>
              <p>{selectedSymbol.toUpperCase()}</p>
            </div>
          )}
        </>
      )}
      {showOrderModal && (
        <div className="order-modal-overlay">
          <div className="order-modal-content">
            <TimeLimit
              symbol={selectedSymbol ?? undefined}
              price={latestValue ?? undefined}
              type={orderType ?? undefined}
              onClose={() => setShowOrderModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Market;
